/**
 * src/lib/api/translations.js — TICKET-959 client for bilingual content.
 * translations.translate: translate rows of a table into a language (only missing/stale).
 * translations.edit: the CEO edit rule — an edit in the shown language becomes the source.
 */
import { apiFetch } from './client';
import { supabase } from '../supabase';
import { TRANSLATABLE_FIELDS } from '../../../lib/quote-doc/translations.mjs';

const D = (action) => `/api/data?action=${action}`;

/** @returns {Promise<{ success, translated_rows, failed_rows } | { success:false, killed:true }>} */
export function translateContent({ table, ids, lang, fallbackLang } = {}) {
  return apiFetch(D('translations.translate'), {
    method: 'POST',
    body: JSON.stringify({ table, ids, lang, fallback_lang: fallbackLang }),
  });
}

/** @returns {Promise<{ success, row }>} the updated row (id, translations, text fields) */
export function editContent({ table, id, lang, edits, fallbackLang } = {}) {
  return apiFetch(D('translations.edit'), {
    method: 'POST',
    body: JSON.stringify({ table, id, lang, edits, fallback_lang: fallbackLang }),
  });
}

/** Re-read one row's text fields + translations (after a translate or an edit). */
export async function fetchTranslatableRow(table, id) {
  const fields = TRANSLATABLE_FIELDS[table];
  if (!fields || !id) return null;
  const { data, error } = await supabase.from(table).select(['id', 'translations', ...fields].join(', ')).eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data || null;
}

/**
 * TICKET-959 D: re-read the stored copies of many rows in ONE select (after a translate),
 * instead of N single fetches. @returns {Promise<object[]>} rows of id, translations, text fields
 */
export async function fetchTranslatableRows({ table, ids } = {}) {
  const fields = TRANSLATABLE_FIELDS[table];
  if (!fields || !ids?.length) return [];
  const { data, error } = await supabase
    .from(table).select(['id', 'translations', ...fields].join(', ')).in('id', ids.slice(0, 200));
  if (error) throw new Error(error.message);
  return data || [];
}
